import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import { NOTIFICATIONS_UPDATED_EVENT } from '../../utils/notificationEvents';
import BrandLogo from './BrandLogo';
import BottomNav, { DesktopNav } from './BottomNav';
import TourButton from '../tour/TourButton';
import './Header.css';

/**
 * Header Component
 * Reusable header/nav bar with branding, navigation and a profile dropdown.
 */
const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    if (!user) return;

    let isActive = true;

    const fetchUnreadCount = async () => {
      try {
        const response = await apiService.get('/notifications/unread_count');
        const value = Number(response?.data?.unread_count || 0);
        if (isActive) setUnreadCount(value);
      } catch (_err) {
        if (isActive) setUnreadCount(0);
      }
    };

    fetchUnreadCount();
    const intervalId = window.setInterval(fetchUnreadCount, 45000);
    const onNotificationsUpdated = () => fetchUnreadCount();
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, onNotificationsUpdated);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, onNotificationsUpdated);
    };
  }, [user]);

  const nickname = (user?.nickname || user?.profile?.nickname || user?.username || '').trim();
  const nicknameParts = nickname.split(/\s+/).filter(Boolean);
  const initials = nicknameParts.length >= 2
    ? `${nicknameParts[0][0]}${nicknameParts[1][0]}`
    : (nickname.slice(0, 2) || 'IK');

  return (
    <header className="header">
      <div className="container header__content">
        <div className="header__brand">
          <Link to="/dashboard" className="header__brand-link" aria-label="Go to iKompete dashboard">
            <BrandLogo adaptive />
          </Link>
        </div>

        <DesktopNav isAdmin={user?.role === 'admin'} />

        <div className={`header__user ${open ? 'header__user--open' : ''}`} ref={dropdownRef}>
          <TourButton onStart={() => setOpen(false)} />

          <Link to="/profile/notifications" className="header__notify-btn" aria-label="Open notifications" data-tour="notifications">
            <span className="header__notify-icon" aria-hidden="true">🔔</span>
            {unreadCount > 0 && (
              <span className="header__notify-badge" aria-label={`${unreadCount} unread notifications`}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>

          <button
            className="header__user-btn"
            aria-label={`Account menu for ${nickname || user?.username || 'you'}`}
            onClick={() => setOpen(o => !o)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(o => !o);
              }
              if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            aria-haspopup="menu"
            aria-expanded={open}
            type="button"
            data-tour="account-menu"
          >
            <span className="header__avatar">{initials.toUpperCase()}</span>
            <div className="header__user-info">
              <span className="header__user-name">{nickname || user?.username}</span>
            </div>
            <span className={`header__chevron ${open ? 'header__chevron--open' : ''}`} aria-hidden="true">⌄</span>
          </button>

          <div className="header__dropdown" role="menu" aria-hidden={!open}>
            <Link to="/profile" className="header__dropdown-item" onClick={() => setOpen(false)}>Profile</Link>
            <Link to="/profile/settings" className="header__dropdown-item" onClick={() => setOpen(false)}>Settings</Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="header__dropdown-item" onClick={() => setOpen(false)}>Admin</Link>
            )}
            <div className="header__dropdown-divider" />
            <button className="header__dropdown-item header__dropdown-logout" onClick={() => { setOpen(false); handleLogout(); }}>Sign out</button>
          </div>
        </div>
      </div>
      {/* Portaled: the header's backdrop-filter would make it the containing block for position: fixed. */}
      {createPortal(<BottomNav isAdmin={user?.role === 'admin'} unreadCount={unreadCount} />, document.body)}
    </header>
  );
};

export default Header;
